import React from "react";
import { Plus, Trash } from "@phosphor-icons/react";

const BOX = "w-full border-2 border-black bg-white px-2 py-1 text-sm";

function InheritedRow({ c, label, i, replaced }) {
  return (
    <div className="mb-1 text-[11px] text-zinc-600" data-testid={`lead-share-custom-default-${i}`}>
      From the default list:{" "}
      <span className={replaced ? "line-through" : ""}>
        <code className="font-bold">{c.name}</code> = {c.source === "field" ? `same as ${label(c.field)}` : `"${c.value}"`}
      </span>
      {replaced && <span className="ml-1 font-bold" data-testid={`lead-share-custom-default-replaced-${i}`}>(replaced for this buyer below)</span>}
    </div>
  );
}

// Fields a buyer's system needs that we don't collect: a fixed answer, or copied from one of our fields.
export function CustomFields({ cat, rows, inherited, onChange }) {
  const fields = [...cat.always_fields, ...cat.share_groups.flatMap((g) => g.fields)];
  const label = (k) => fields.find((f) => f.key === k)?.label || k;
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="mb-4 border-2 border-dashed border-black p-3" data-testid="lead-share-custom">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <div className="label-mono">Extra fields for their system</div>
        <button type="button" onClick={() => onChange([...rows, { name: "", source: "fixed", value: "", field: null }])}
                className="ml-auto inline-flex items-center gap-1 border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white"
                data-testid="lead-share-custom-add">
          <Plus size={11} weight="bold" /> Add a field
        </button>
      </div>
      <p className="mb-2 text-[11px] text-zinc-600">
        For a field their system expects that we don&apos;t have. Type its name, then either a fixed answer sent on every lead or one of our fields to copy the answer from. Used in web posts and file drops.
      </p>
      {inherited.map((c, i) => <InheritedRow key={c.name} c={c} label={label} i={i} replaced={rows.some((r) => r.name.trim() === c.name)} />)}
      {rows.map((r, i) => (
        <div key={i} className="mb-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_160px_minmax(0,1fr)_auto]" data-testid={`lead-share-custom-row-${i}`}>
          <input value={r.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Their field name, e.g. Source" maxLength={80}
                 className={`${BOX} font-mono`} data-testid={`lead-share-custom-name-${i}`} />
          <select value={r.source} onChange={(e) => set(i, { source: e.target.value })} className={BOX} data-testid={`lead-share-custom-source-${i}`}>
            <option value="fixed">Fixed answer</option>
            <option value="field">Copy one of our fields</option>
          </select>
          {r.source === "field" ? (
            <select value={r.field || ""} onChange={(e) => set(i, { field: e.target.value || null })} className={BOX} data-testid={`lead-share-custom-field-${i}`}>
              <option value="">Pick a field…</option>
              {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          ) : (
            <input value={r.value} onChange={(e) => set(i, { value: e.target.value })} placeholder="Answer sent on every lead" maxLength={500}
                   className={BOX} data-testid={`lead-share-custom-value-${i}`} />
          )}
          <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove this field"
                  className="justify-self-start border-2 border-black bg-white px-2 py-1 transition-colors duration-100 hover:bg-red-600 hover:text-white"
                  data-testid={`lead-share-custom-remove-${i}`}>
            <Trash size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}
